import { useParams, useSearchParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { useCustomerDetail } from '@/api/customers';
import { CustomerDetailHeader } from './customer-detail-header';
import { BasicInformationTab } from './basic-information-tab';
import { SaleProcessTab } from './sale-process/SaleProcessTab';
import { ContractsTab } from './contracts-tab';
import { InvoicesTab } from './invoices-tab';
import { ServiceTab } from './service-tab';
import SlaTicketsTab from './sla-tickets/SlaTicketsTab';
import MailerTab from './mailer/MailerTab';
import ActivityLogsTab from './activity-logs/ActivityLogsTab';
import { RevenueAllocationTab } from './revenue-allocation-tab';
import { CreditsTab } from './credits-tab';
import { TabError, TabLoading } from './tab-states';

const TABS = [
  { value: 'basic-information', label: 'Basic Information' },
  { value: 'sale-process', label: 'Sale Process' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'service', label: 'Services & Resources' },
  { value: 'sla-tickets', label: 'SLA & Tickets' },
  { value: 'mailer', label: 'Mailer' },
  { value: 'activity-logs', label: 'Activity Logs' },
  { value: 'revenue-allocation', label: 'Revenue Allocation' },
  { value: 'credits', label: 'Credits' },
] as const;

const TAB_VALUES = TABS.map((t) => t.value) as readonly string[];

/**
 * Customer detail page — record header (id-NAME + status pill + lead-type badge
 * + back-to-list) over a Tabs switcher persisted in `?tab=`. Remaining legacy
 * tabs (SLA & Tickets, Mailers, Activity Logs, Revenue Allocation, Credits,
 * Documents, Negotiation) land in later waves.
 */
export default function CustomerDetailPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam) || 0;
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab && TAB_VALUES.includes(requestedTab) ? requestedTab : 'basic-information';

  const { data, isLoading, isError, refetch } = useCustomerDetail(id);
  const detail = data?.result;

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="full">
      {isError ? (
        <TabError title="Failed to load customer" onRetry={() => refetch()} />
      ) : isLoading || !detail ? (
        <TabLoading rows={4} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <CustomerDetailHeader detail={detail} />

          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList>
              {TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="basic-information">
              <BasicInformationTab detail={detail} />
            </TabsContent>
            <TabsContent value="sale-process">
              {activeTab === 'sale-process' ? <SaleProcessTab customerId={id} /> : null}
            </TabsContent>
            <TabsContent value="contracts">
              {activeTab === 'contracts' ? (
                <ContractsTab id={id} />
              ) : null}
            </TabsContent>
            <TabsContent value="invoices">
              {activeTab === 'invoices' ? <InvoicesTab id={id} /> : null}
            </TabsContent>
            <TabsContent value="service">
              {activeTab === 'service' ? <ServiceTab id={id} /> : null}
            </TabsContent>
            <TabsContent value="sla-tickets">
              {activeTab === 'sla-tickets' ? <SlaTicketsTab customerId={id} /> : null}
            </TabsContent>
            <TabsContent value="mailer">
              {activeTab === 'mailer' ? <MailerTab customerId={id} /> : null}
            </TabsContent>
            <TabsContent value="activity-logs">
              {activeTab === 'activity-logs' ? <ActivityLogsTab customerId={id} /> : null}
            </TabsContent>
            <TabsContent value="revenue-allocation">
              {activeTab === 'revenue-allocation' ? <RevenueAllocationTab id={id} /> : null}
            </TabsContent>
            <TabsContent value="credits">
              {activeTab === 'credits' ? <CreditsTab customerId={id} /> : null}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Container>
  );
}
